import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MovieCard from '../components/MovieCard';
import Sidebar from '../components/layout/Sidebar';
import PageHeader from '../components/PageHeader';
import QuestionnaireModal from '../components/QuestionnaireModal';
import { getRecommendations, getUserInfo, handleAPIError } from '../services/api';
import '../styles/home.css';

const Home = () => {
  const navigate = useNavigate();
  const [movies, setMovies] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all'); // 'all', 'movies', 'series'
  const [sortType, setSortType] = useState('best_match'); // 'best_match', 'host', 'release_new', 'release_old'

  // Header actions for recommendations page - recreated when state changes
  const headerActions = useMemo(() => [
    {
      label: 'Sort By',
      icon: 'fa-solid fa-sort',
      dropdown: [
        {
          label: 'Best Match',
          onClick: () => setSortType('best_match'),
          isSelected: sortType === 'best_match'
        },
        {
          label: 'Host / Service',
          onClick: () => setSortType('host'),
          isSelected: sortType === 'host'
        },
        {
          label: 'Release Date (Newest First)',
          onClick: () => setSortType('release_new'),
          isSelected: sortType === 'release_new'
        },
        {
          label: 'Release Date (Oldest First)',
          onClick: () => setSortType('release_old'),
          isSelected: sortType === 'release_old'
        }
      ]
    },
    {
      label: 'Filter',
      icon: 'fa-solid fa-filter',
      dropdown: [
        {
          label: 'Show All',
          onClick: () => {
            console.log('Show All clicked, current filterType:', filterType);
            setFilterType('all');
            console.log('Setting filterType to all');
          },
          isSelected: filterType === 'all'
        },
        {
          label: 'Movies Only',
          onClick: () => {
            console.log('Movies Only clicked, current filterType:', filterType);
            setFilterType('movies');
            console.log('Setting filterType to movies');
          },
          isSelected: filterType === 'movies'
        },
        {
          label: 'Series Only',
          onClick: () => {
            console.log('Series Only clicked, current filterType:', filterType);
            setFilterType('series');
            console.log('Setting filterType to series');
          },
          isSelected: filterType === 'series'
        }
      ]
    },
    {
      label: 'Refine',
      icon: 'fa-solid fa-rotate',
      onClick: () => window.location.reload()
    },
    {
      label: 'Preferences',
      icon: 'fa-solid fa-clipboard-question',
      onClick: () => navigate('/full-questionnaire/')
    }
  ], [sortType, filterType, navigate]);

  // Filter and sort movies based on current filter and sort types
  const processedMovies = movies
    .filter(movie => {
      if (filterType === 'all') return true;
      if (filterType === 'movies') return movie.movie === '1';
      if (filterType === 'series') return movie.movie === '0'; // TV shows have movie == '0'
      return true;
    })
    .sort((a, b) => {
      switch (sortType) {
        case 'best_match':
          // Keep original order (recommendations are already sorted by score)
          return 0;

        case 'host':
          // Sort by streaming service (dist field)
          const aDist = a.dist || 'ZZZ';
          const bDist = b.dist || 'ZZZ';
          return aDist.localeCompare(bDist);
        case 'release_new':
          // Sort by release date, newest first
          const aDate = a.earliest_release || new Date('2099-12-12');
          const bDate = b.earliest_release || new Date('2099-12-12');
          return new Date(bDate) - new Date(aDate);
        case 'release_old':
          // Sort by release date, oldest first
          const aDateOld = a.earliest_release || new Date('2099-12-12');
          const bDateOld = b.earliest_release || new Date('2099-12-12');
          return new Date(aDateOld) - new Date(bDateOld);
        default:
          return 0;
      }
    });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch user info and recommendations in parallel
        const [userData, recommendationsData] = await Promise.all([
          getUserInfo(),
          getRecommendations()
        ]);

        setUser(userData);
        setMovies(recommendationsData.titles || []);
        setError(null);
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.message.includes('401') || error.message.includes('403')) {
          // Redirect to login for authentication errors
          navigate('/auth');
        } else {
          setError('Failed to load recommendations. Please try again.');
          handleAPIError(error);
        }
      } finally {
    setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Debug filterType changes
  useEffect(() => {
    console.log('filterType changed to:', filterType);
  }, [filterType]);

  const handlePreferenceChange = async (imdbId, preference, responseData) => {
    setMovies(prevMovies => 
      prevMovies.map(movie => {
        if (movie.imdb_id === imdbId) {
          // Update the movie's preference state based on the response
          const updatedMovie = { ...movie };
          
          // Clear all preference flags first
          updatedMovie.isfavorite = null;
          updatedMovie.watched = null;
          updatedMovie.not_interested = null;
          updatedMovie.seen_and_liked = null;
          updatedMovie.seen_and_disliked = null;
          
          // Set the appropriate flag based on the preference
          switch (preference) {
            case 1:
              updatedMovie.isfavorite = '1';
              break;
            case -1:
              updatedMovie.isfavorite = null;
              break;
            case 2:
              updatedMovie.watched = '2';
              break;
            case -2:
              updatedMovie.watched = null;
              break;
            case 3:
              updatedMovie.not_interested = '3';
              break;
            case -3:
              updatedMovie.not_interested = null;
              break;
            case 4:
              updatedMovie.seen_and_liked = '4';
              updatedMovie.seen_and_disliked = null; // Clear thumbs down when thumbs up is selected
              break;
            case -4:
              updatedMovie.seen_and_liked = null;
              break;
            case 5:
              updatedMovie.seen_and_disliked = '5';
              updatedMovie.seen_and_liked = null; // Clear thumbs up when thumbs down is selected
              break;
            case -5:
              updatedMovie.seen_and_disliked = null;
              break;
            default:
              break;
          }
          
          return updatedMovie;
        }
        return movie;
      })
    );
  };

  if (loading) {
    return (
      <>
        <Sidebar user={user} />
        <div className="main-sec">
          <div className="home-container">
            <PageHeader title="My Recommendations" actions={headerActions} />
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading recommendations...</p>
            </div>
          </div>
        </div>
        
        {/* Questionnaire Modal - also show in loading state */}
        <QuestionnaireModal />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Sidebar user={user} />
        <div className="main-sec">
          <div className="home-container">
            <PageHeader title="My Recommendations" actions={headerActions} />
            
            <div className="error-message">
              <h2>Oops! Something went wrong</h2>
              <p>{error}</p>
              <button onClick={() => window.location.reload()}>Try Again</button>
            </div>
          </div>
        </div>
        
        {/* Questionnaire Modal - also show in error state */}
        <QuestionnaireModal />
      </>
    );
  }

  return (
    <>
      <Sidebar user={user} />
      <div className="main-sec">
        <div className="home-container">
          <PageHeader title="My Recommendations" actions={headerActions} />
          
          <div className="movies-grid">
            {processedMovies.map(movie => (
              <MovieCard
                key={movie.imdb_id}
                movie={movie}
                onPreferenceChange={handlePreferenceChange}
              />
            ))}
          </div>
          
          {processedMovies.length === 0 && (
            <div className="empty-state">
              <h2>No recommendations found</h2>
              <p>
                {filterType === 'movies' && 'There are no movies in your current results list.'}
                {filterType === 'series' && 'There are no series in your current results list.'}
                {filterType === 'all' && 'Complete your questionnaire to get personalized recommendations!'}
              </p>
              {filterType !== 'all' && (
                <button className="btn btn-primary" onClick={() => setFilterType('all')}>
                  Show All Results
                </button>
              )}
              {filterType === 'all' && (
                <button className="btn btn-primary">Take Questionnaire</button>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Questionnaire Modal */}
      <QuestionnaireModal />
    </>
  );
};

export default Home; 